import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import WithdrawalStatusMessage, { WITHDRAWAL_FAIL_REASONS, CASHOUT_REJECT_REASONS } from '../components/WithdrawalStatusMessage';
import {
  getCashoutApplicationDetail,
  reviewCashoutApplication,
  getUserWithdrawalRequests,
  updateWithdrawalRequest,
  CashoutApplicationDetail,
  CashoutApplicationEligibility,
  CashoutPayoutMethodSummary,
  CashoutPolicyViolation,
  CashoutWalletTransaction,
  WithdrawalRequest,
} from '../services/cashoutApi';

const formatDate = (value?: string | number | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatNumber = (value?: number | null) => Number(value || 0).toLocaleString();
const formatCurrency = (value?: number | null) => `$${Number(value || 0).toFixed(2)}`;

const formatLabel = (value?: string | null) => {
  if (!value) return 'N/A';
  return value.replace(/_/g, ' ');
};

const TABLE_SCROLL_STEP_PX = 360;

const scrollTableRows = (ref: React.RefObject<HTMLDivElement>, direction: 1 | -1) => {
  ref.current?.scrollBy({ top: direction * TABLE_SCROLL_STEP_PX, behavior: 'smooth' });
};

const getStatusBadgeClass = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const getWdStatusBadgeClass = (status: string) => {
  if (status === 'processing') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'failed') return 'bg-rose-100 text-rose-700 border-rose-200';
  if (status === 'cancelled') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const EligibilityRow: React.FC<{ label: string; met: boolean; detail?: string }> = ({ label, met, detail }) => (
  <div className='flex items-start justify-between gap-2 rounded-lg border border-gray-200 px-3 py-3'>
    <div className='min-w-0'>
      <p className='text-sm font-semibold text-gray-900'>{label}</p>
      {detail && <p className='mt-0.5 text-xs text-gray-500'>{detail}</p>}
    </div>
    <span className={`shrink-0 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${met ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
      {met ? 'Met' : 'Missing'}
    </span>
  </div>
);

const BalanceMetric: React.FC<{ label: string; value: number; emphasis?: boolean }> = ({ label, value, emphasis }) => (
  <div className='rounded-lg border border-gray-200 bg-white px-3 py-3'>
    <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
    <p className={`mt-1 text-lg font-bold leading-tight ${emphasis ? 'text-gray-950' : 'text-gray-800'}`}>{formatNumber(value)}</p>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className='flex items-start justify-between gap-3 border-b border-gray-100 py-1.5 text-sm last:border-b-0'>
    <span className='text-gray-500'>{label}</span>
    <span className='break-all text-right font-semibold text-gray-800'>{value ?? 'N/A'}</span>
  </div>
);

const SummaryTile: React.FC<{ label: string; value: React.ReactNode; detail?: React.ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' }> = ({
  label,
  value,
  detail,
  tone = 'neutral',
}) => {
  const toneClass =
    tone === 'good'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : tone === 'bad'
          ? 'border-rose-200 bg-rose-50 text-rose-900'
          : 'border-gray-200 bg-white text-gray-900';

  return (
    <div className={`rounded-lg border px-4 py-3 ${toneClass}`}>
      <p className='text-[11px] font-semibold uppercase tracking-wide opacity-70'>{label}</p>
      <p className='mt-1 text-lg font-bold leading-tight'>{value}</p>
      {detail && <p className='mt-1 text-xs opacity-75'>{detail}</p>}
    </div>
  );
};

const SectionShell: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; className?: string }> = ({
  title,
  subtitle,
  children,
  className = '',
}) => (
  <section className={`rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}>
    <div className='border-b border-gray-100 px-5 py-4'>
      <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
      {subtitle && <p className='mt-0.5 text-sm text-gray-500'>{subtitle}</p>}
    </div>
    <div className='p-5'>{children}</div>
  </section>
);

const TableScrollFooter: React.FC<{ label: string; onPrevious: () => void; onNext: () => void }> = ({
  label,
  onPrevious,
  onNext,
}) => (
  <div className='flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between'>
    <span className='text-xs font-medium text-gray-500'>{label}</span>
    <div className='flex items-center gap-2'>
      <button
        type='button'
        onClick={onPrevious}
        className='rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100'
      >
        Previous rows
      </button>
      <button
        type='button'
        onClick={onNext}
        className='rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800'
      >
        Next rows
      </button>
    </div>
  </div>
);

const TransactionTable: React.FC<{ title: string; transactions: CashoutWalletTransaction[]; unitLabel: 'token' | 'coin' }> = ({
  title,
  transactions,
  unitLabel,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <div className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'>
      <div className='border-b border-gray-200 px-5 py-4'>
        <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
        <p className='text-sm text-gray-500'>Last 20 {unitLabel} wallet transactions.</p>
      </div>
      {transactions.length === 0 ? (
        <div className='px-5 py-8 text-center text-sm text-gray-400'>No {unitLabel} transactions found.</div>
      ) : (
        <>
          <div ref={tableRef} className='max-h-[440px] overflow-y-auto'>
            <table className='w-full table-fixed divide-y divide-gray-200'>
              <thead className='sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_rgba(229,231,235,1)]'>
                <tr>
                  <th className='w-[17%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Date</th>
                  <th className='w-[13%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Type</th>
                  <th className='w-[13%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Source</th>
                  <th className='w-[10%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Amount</th>
                  <th className='w-[12%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                  <th className='w-[35%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Description</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100 bg-white'>
                {transactions.map((tx) => {
                  const isDebit = tx.transaction_type === 'debit';
                  const bucket = unitLabel === 'token' ? tx.token_type : tx.coin_type;
                  return (
                    <tr key={tx.transaction_id} className='hover:bg-gray-50/50'>
                      <td className='px-4 py-3.5 text-sm text-gray-500'>{formatDate(tx.created_at)}</td>
                      <td className='px-4 py-3.5 text-sm'>
                        <p className='font-medium capitalize text-gray-800'>{formatLabel(tx.transaction_type)}</p>
                        <p className='text-xs capitalize text-gray-400'>{formatLabel(bucket)}</p>
                      </td>
                      <td className='px-4 py-3.5 text-sm capitalize text-gray-600'>{formatLabel(tx.transaction_source)}</td>
                      <td className={`px-4 py-3.5 text-sm font-semibold ${isDebit ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {isDebit ? '-' : '+'}{formatNumber(tx.amount)}
                      </td>
                      <td className='px-4 py-3.5 text-sm capitalize text-gray-600'>{formatLabel(tx.transaction_status)}</td>
                      <td className='px-4 py-3.5 text-sm text-gray-600'>
                        <p className='font-medium text-gray-800'>{tx.title || 'N/A'}</p>
                        {tx.description && <p className='mt-0.5 line-clamp-2 text-xs text-gray-500'>{tx.description}</p>}
                        <p className='mt-1 break-all font-mono text-[11px] text-gray-400'>{tx.transaction_id}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <TableScrollFooter
            label={`${transactions.length} rows in this table`}
            onPrevious={() => scrollTableRows(tableRef, -1)}
            onNext={() => scrollTableRows(tableRef, 1)}
          />
        </>
      )}
    </div>
  );
};

const PayoutMethodsPanel: React.FC<{ methods: CashoutPayoutMethodSummary[] }> = ({ methods }) => (
  <SectionShell title='Payout Methods' subtitle={`${methods.length} active method${methods.length === 1 ? '' : 's'}`}>
    {methods.length === 0 ? (
      <p className='py-4 text-center text-sm text-gray-400'>No active payout methods.</p>
    ) : (
      <div className='space-y-3'>
        {methods.map((method) => (
          <div key={method.method_id} className='rounded-lg border border-gray-200 bg-gray-50/50 p-3'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <p className='font-semibold text-gray-900'>{method.display_name || method.type}</p>
                <p className='text-xs capitalize text-gray-500'>{formatLabel(method.type)}</p>
              </div>
              <div className='flex flex-wrap justify-end gap-2'>
                {method.is_default && <span className='rounded-full bg-gray-900 px-2 py-0.5 text-xs font-semibold text-white'>Default</span>}
                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${getStatusBadgeClass(method.verification_status)}`}>
                  {method.verification_status}
                </span>
              </div>
            </div>
            <div className='mt-3 grid gap-2 text-sm sm:grid-cols-2'>
              {method.account_holder_name && <DetailRow label='Holder' value={method.account_holder_name} />}
              {method.bank_name && <DetailRow label='Bank' value={method.bank_name} />}
              {method.account_number_last4 && <DetailRow label='Last 4' value={`****${method.account_number_last4}`} />}
              {method.currency_code && <DetailRow label='Currency' value={method.currency_code} />}
              {method.bank_country && <DetailRow label='Bank Country' value={method.bank_country} />}
              {method.upi_id && <DetailRow label='UPI' value={method.upi_id} />}
              {method.paypal_email && <DetailRow label='PayPal' value={method.paypal_email} />}
              {method.card_last4 && <DetailRow label='Card' value={`${method.card_brand || 'Card'} ****${method.card_last4}`} />}
            </div>
            {method.verification_note && <p className='mt-2 text-xs text-gray-500'>{method.verification_note}</p>}
          </div>
        ))}
      </div>
    )}
  </SectionShell>
);

const PolicyViolationsPanel: React.FC<{ violations: CashoutPolicyViolation[] }> = ({ violations }) => (
  <SectionShell title='Policy Violations' subtitle={violations.length ? `${violations.length} profile violation${violations.length === 1 ? '' : 's'}` : 'No profile violations'}>
    {violations.length === 0 ? (
      <p className='py-4 text-center text-sm text-gray-400'>No policy violations on profile.</p>
    ) : (
      <div className='space-y-3'>
        {violations.map((violation, idx) => (
          <div key={violation.violation_id || idx} className='rounded-lg border border-rose-100 bg-rose-50 p-3'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <p className='font-semibold text-rose-900'>{violation.code || 'Violation'}</p>
                <p className='mt-0.5 text-sm text-rose-700'>{violation.reason || 'No reason provided'}</p>
              </div>
              <span className='rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-rose-700'>{violation.source || 'N/A'}</span>
            </div>
            <div className='mt-2 grid gap-2 text-xs text-rose-700 sm:grid-cols-2'>
              <span>Created: {formatDate(violation.created_at)}</span>
              <span>Expires: {formatDate(violation.expires_at)}</span>
              {violation.video_id && <span className='font-mono'>Video: {violation.video_id}</span>}
              {violation.moderator_user_id && <span className='font-mono'>Moderator: {violation.moderator_user_id}</span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </SectionShell>
);

const buildEligibilityRows = (e: CashoutApplicationEligibility) => [
  { label: 'Account Verified', met: e.account_verified, detail: 'Email + phone verified' },
  { label: 'Plus Subscription', met: e.plus_subscription_active, detail: 'Active Plus membership' },
  { label: 'Invites', met: e.invites.completed >= e.invites.required, detail: `${e.invites.completed} / ${e.invites.required} accepted` },
  { label: 'Video Uploaded', met: e.video_uploaded, detail: 'At least 1 video posted' },
  { label: 'Streak', met: e.streak.current_days >= e.streak.required_days, detail: `${e.streak.current_days} / ${e.streak.required_days} days` },
  { label: 'No Policy Violations', met: e.no_policy_violations, detail: 'Account in good standing' },
  { label: 'Payout Method', met: e.payout_method_added, detail: 'Bank / payout account added in-app' },
];

const CashoutApplicationDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<CashoutApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [note, setNote] = useState('');

  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [wdLoading, setWdLoading] = useState(false);
  const [wdActionLoading, setWdActionLoading] = useState('');
  const [failingRequestId, setFailingRequestId] = useState<string | null>(null);
  const [failReason, setFailReason] = useState('');
  const withdrawalTableRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getCashoutApplicationDetail(userId);
      setDetail(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load application details');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadWithdrawals = useCallback(async () => {
    if (!userId) return;
    setWdLoading(true);
    try {
      const data = await getUserWithdrawalRequests(userId);
      setWithdrawals(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load withdrawal history');
    } finally {
      setWdLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
    loadWithdrawals();
  }, [load, loadWithdrawals]);

  const handleApproveWithdrawal = async (requestId: string) => {
    setWdActionLoading(`${requestId}:complete`);
    try {
      await updateWithdrawalRequest(requestId, 'completed');
      toast.success('Withdrawal marked as completed');
      await loadWithdrawals();
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete withdrawal');
    } finally {
      setWdActionLoading('');
    }
  };

  const handleFailWithdrawal = async (requestId: string) => {
    if (!failReason.trim()) {
      toast.error('A failure reason is required');
      return;
    }
    setWdActionLoading(`${requestId}:fail`);
    try {
      await updateWithdrawalRequest(requestId, 'failed', failReason.trim());
      toast.success('Withdrawal marked as failed');
      setFailingRequestId(null);
      setFailReason('');
      await loadWithdrawals();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update withdrawal');
    } finally {
      setWdActionLoading('');
    }
  };

  const handleReview = async (status: 'APPROVED' | 'REJECTED' | 'PENDING') => {
    if (!userId) return;
    if (status === 'REJECTED' && !note.trim()) {
      toast.error('A rejection reason is required');
      return;
    }
    setActionLoading(status);
    try {
      await reviewCashoutApplication(userId, status, note.trim() || undefined);
      toast.success(status === 'PENDING' ? 'Application reset to pending' : `Application ${status.toLowerCase()}`);
      await load();
      setNote('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to review application');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500'>Application not found.</p>
          <button
            onClick={() => navigate('/cashout')}
            className='mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
          >
            ← Back to Cashout
          </button>
        </div>
      </div>
    );
  }

  const { application, profile, eligibility, plus, wallets, payout_methods, withdrawal_summary, transactions } = detail;
  const tokenWallet = wallets?.tokens;
  const coinWallet = wallets?.coins;
  const tokenTransactions = transactions?.tokens || [];
  const coinTransactions = transactions?.coins || [];
  const notableTokenTransactions = transactions?.notable_tokens || [];
  const notableCoinTransactions = transactions?.notable_coins || [];
  const payoutMethods = payout_methods || [];
  const policyViolations = profile?.policy_violations || [];
  const displayName = profile?.name || profile?.user_name || application?.user_name || 'Unknown User';
  const username = profile?.user_name ? `@${profile.user_name}` : null;
  const isPending = application?.status === 'PENDING';
  const isApproved = application?.status === 'APPROVED';
  const isRejected = application?.status === 'REJECTED';
  const eligibilityRows = buildEligibilityRows(eligibility);
  const metCount = eligibilityRows.filter((r) => r.met).length;
  const withdrawalSummary = withdrawals.reduce(
    (acc, wd) => ({
      count: acc.count + 1,
      tokens: acc.tokens + Number(wd.tokens || 0),
      grossUsd: acc.grossUsd + Number(wd.gross_usd || 0),
      netUsd: acc.netUsd + Number(wd.net_usd || 0),
      processing: acc.processing + (wd.status === 'processing' ? 1 : 0),
      completed: acc.completed + (wd.status === 'completed' ? 1 : 0),
      failed: acc.failed + (wd.status === 'failed' ? 1 : 0),
    }),
    { count: 0, tokens: 0, grossUsd: 0, netUsd: 0, processing: 0, completed: 0, failed: 0 },
  );
  const lifetimeWithdrawalSummary = withdrawal_summary || {
    total_requests: withdrawalSummary.count,
    total_tokens: withdrawalSummary.tokens,
    total_net_usd: withdrawalSummary.netUsd,
    processing_count: withdrawalSummary.processing,
    completed_count: withdrawalSummary.completed,
    failed_count: withdrawalSummary.failed,
    cancelled_count: 0,
    completed_tokens: 0,
    processing_tokens: 0,
    failed_tokens: 0,
    cancelled_tokens: 0,
    completed_net_usd: 0,
    processing_net_usd: 0,
    failed_net_usd: 0,
    cancelled_net_usd: 0,
    total_gross_usd: withdrawalSummary.grossUsd,
  };
  const eligibilityTone: 'good' | 'bad' = eligibility.is_eligible ? 'good' : 'bad';
  const applicationTone: 'good' | 'warn' | 'bad' = isApproved ? 'good' : isRejected ? 'bad' : 'warn';
  const plusTone: 'good' | 'warn' = plus?.has_paid_plus ? 'good' : 'warn';
  const policyTone: 'good' | 'bad' = policyViolations.length ? 'bad' : 'good';

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Back */}
        <button
          onClick={() => navigate('/cashout')}
          className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Cashout
        </button>

        {/* Header card */}
        <div className='mb-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex items-center gap-4'>
              {profile?.profile_image_url ? (
                <img
                  src={profile.profile_image_url}
                  alt={displayName}
                  className='h-16 w-16 rounded-full object-cover shrink-0'
                />
              ) : (
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] text-2xl font-bold text-white shrink-0'>
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className='text-xl font-bold text-gray-900'>{displayName}</h1>
                {username && <p className='text-sm text-gray-500'>{username}</p>}
                <p className='mt-0.5 text-xs text-gray-400 font-mono'>{userId}</p>
              </div>
            </div>
            <div className='flex flex-wrap items-center gap-3'>
              {application && (
                <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${getStatusBadgeClass(application.status)}`}>
                  {application.status}
                </span>
              )}
              <button
                onClick={() => navigate(`/bulk-review?userId=${encodeURIComponent(userId || '')}&userName=${encodeURIComponent(profile?.user_name || '')}`)}
                className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors'
              >
                See User Videos →
              </button>
            </div>
          </div>

          {application && (
            <div className='mt-4 flex flex-wrap gap-4 border-t border-gray-100 pt-4 text-sm text-gray-600'>
              <span><span className='font-semibold text-gray-800'>Applied:</span> {formatDate(application.applied_at)}</span>
              {application.reviewed_at && (
                <span><span className='font-semibold text-gray-800'>Reviewed:</span> {formatDate(application.reviewed_at)}</span>
              )}
              {application.note && (
                <span><span className='font-semibold text-gray-800'>Note:</span> {application.note}</span>
              )}
            </div>
          )}
        </div>

        <div className='mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-6'>
          <SummaryTile label='Application' value={application?.status || 'N/A'} detail={formatDate(application?.applied_at)} tone={applicationTone} />
          <SummaryTile label='Eligibility' value={eligibility.is_eligible ? 'Eligible' : `${metCount}/${eligibility.progress.required}`} detail='criteria met' tone={eligibilityTone} />
          <SummaryTile label='Paid Plus' value={plus?.has_paid_plus ? 'Yes' : 'No'} detail={formatDate(plus?.expiration_date)} tone={plusTone} />
          <SummaryTile label='Token Balance' value={formatNumber(tokenWallet?.total_tokens || 0)} detail='available wallet' />
          <SummaryTile label='Lifetime Net' value={formatCurrency(lifetimeWithdrawalSummary.total_net_usd)} detail={`${formatNumber(lifetimeWithdrawalSummary.total_requests)} requests`} />
          <SummaryTile label='Policy' value={policyViolations.length ? `${policyViolations.length} issues` : 'Clear'} detail={profile?.account_status || 'N/A'} tone={policyTone} />
        </div>

        {/* Wallet balances */}
        <div className='mb-5 grid gap-5 lg:grid-cols-2'>
          <SectionShell title='Token Balance' subtitle={`Withdrawable wallet • Last transaction ${formatDate(tokenWallet?.last_transaction_date)}`}>
            <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
              <BalanceMetric label='Total Tokens' value={tokenWallet?.total_tokens || 0} emphasis />
              <BalanceMetric label='Reward' value={tokenWallet?.reward_tokens || 0} />
              <BalanceMetric label='Purchased' value={tokenWallet?.purchased_tokens || 0} />
              <BalanceMetric label='Gifted' value={tokenWallet?.gifted_tokens || 0} />
            </div>
          </SectionShell>

          <SectionShell title='Coin Balance' subtitle={`Legacy wallet • Last transaction ${formatDate(coinWallet?.last_transaction_date)}`}>
            <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
              <BalanceMetric label='Total Coins' value={coinWallet?.total_coins || 0} emphasis />
              <BalanceMetric label='Reward' value={coinWallet?.reward_coins || 0} />
              <BalanceMetric label='Plus Reward' value={coinWallet?.plus_reward_coins || 0} />
              <BalanceMetric label='Purchased' value={coinWallet?.purchased_coins || 0} />
              <BalanceMetric label='Gifted' value={coinWallet?.gifted_coins || 0} />
              <BalanceMetric label='Tips / Referrals' value={(coinWallet?.tip_coins || 0) + (coinWallet?.referral_coins || 0)} />
            </div>
          </SectionShell>
        </div>

        <div className='mb-5 grid gap-5 lg:grid-cols-2'>
          <SectionShell title='Account Risk Context' subtitle='Identity, activity, and device signals'>
            <div className='grid gap-2 sm:grid-cols-2'>
              <DetailRow label='Joined' value={profile?.created_at ? formatDate(profile.created_at) : 'N/A'} />
              <DetailRow label='Last Active' value={formatDate(profile?.last_active_time)} />
              <DetailRow label='Country' value={profile?.country_code || profile?.location || 'N/A'} />
              <DetailRow label='Region' value={profile?.region || 'N/A'} />
              <DetailRow label='Registration Device' value={profile?.registration_device || 'N/A'} />
              <DetailRow label='Registration Type' value={profile?.registration_type || 'N/A'} />
              <DetailRow label='IP Address' value={profile?.ip_address || 'N/A'} />
              <DetailRow label='Dev Account' value={profile?.is_dev_account ? 'Yes' : 'No'} />
              <DetailRow label='Trusted Score' value={formatNumber(profile?.trusted_score)} />
              <DetailRow label='Policy Violations' value={formatNumber(profile?.policy_violations_count)} />
            </div>
          </SectionShell>

          <SectionShell title='Plus Membership' subtitle='Computed status and latest subscription event'>
            <div className='grid gap-2 sm:grid-cols-2'>
              <DetailRow label='Paid Plus' value={plus?.has_paid_plus ? 'Yes' : 'No'} />
              <DetailRow label='Plus Access' value={plus?.has_plus_access ? 'Yes' : 'No'} />
              <DetailRow label='Trial Active' value={plus?.is_trial_active ? 'Yes' : 'No'} />
              <DetailRow label='Computed Expires' value={formatDate(plus?.expiration_date)} />
              <DetailRow label='Profile Product' value={profile?.current_product_id || 'N/A'} />
              <DetailRow label='Latest Product' value={plus?.latest_subscription?.product_id || 'N/A'} />
              <DetailRow label='Latest Status' value={plus?.latest_subscription?.status || 'N/A'} />
              <DetailRow label='Latest Event' value={plus?.latest_subscription?.event_type || 'N/A'} />
              <DetailRow label='Provider' value={plus?.latest_subscription?.provider || 'N/A'} />
              <DetailRow label='Store' value={plus?.latest_subscription?.store || 'N/A'} />
            </div>
          </SectionShell>
        </div>

        <div className='grid gap-5'>
          <SectionShell title='Cashout Eligibility' subtitle={`${metCount} of ${eligibility.progress.required} requirements met`}>
            <div className='mb-4 flex items-center justify-between gap-3'>
              <span className={`rounded-full px-3 py-1 text-sm font-semibold ${eligibility.is_eligible ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {eligibility.is_eligible ? 'All criteria met' : 'Not yet eligible'}
              </span>
              <span className='text-sm font-semibold text-gray-700'>
                {Math.round((metCount / Math.max(eligibility.progress.required, 1)) * 100)}%
              </span>
            </div>

            <div className='mb-2 flex items-center justify-between text-xs text-gray-500'>
              <span>Criteria met</span>
              <span>{metCount} / {eligibility.progress.required}</span>
            </div>
            <div className='h-2 w-full rounded-full bg-gray-100 overflow-hidden'>
              <div
                className='h-full rounded-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] transition-all'
                style={{ width: `${Math.min(100, (metCount / Math.max(eligibility.progress.required, 1)) * 100)}%` }}
              />
            </div>

            <div className='mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
              {eligibilityRows.map((row) => (
                <EligibilityRow key={row.label} label={row.label} met={row.met} detail={row.detail} />
              ))}
            </div>
          </SectionShell>

          <div className='grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]'>
            <SectionShell title='Review' subtitle='Update the application decision'>
              <div className='space-y-3'>
                {!isPending && (
                  <p className='rounded-lg bg-gray-50 px-3 py-2 text-center text-xs font-medium text-gray-500'>
                    Currently {application?.status?.toLowerCase()}. You can change the decision below.
                  </p>
                )}
                <div>
                  <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500'>
                    Rejection Reason
                  </label>
                  <select
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  >
                    <option value=''>— Select reason (required for rejection) —</option>
                    {CASHOUT_REJECT_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                {!isApproved && (
                  <button
                    onClick={() => handleReview('APPROVED')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors'
                  >
                    {actionLoading === 'APPROVED' ? 'Approving…' : isRejected ? 'Re-Approve' : 'Approve'}
                  </button>
                )}
                {!isRejected && (
                  <button
                    onClick={() => handleReview('REJECTED')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors'
                  >
                    {actionLoading === 'REJECTED' ? 'Rejecting…' : isApproved ? 'Re-Reject' : 'Reject'}
                  </button>
                )}
                {!isPending && (
                  <button
                    onClick={() => handleReview('PENDING')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 transition-colors'
                  >
                    {actionLoading === 'PENDING' ? 'Resetting…' : 'Reset to Pending'}
                  </button>
                )}
              </div>
            </SectionShell>

            {profile && (
              <SectionShell title='Identity' subtitle='Core profile fields'>
                <div className='grid gap-x-6 gap-y-1 sm:grid-cols-2 xl:grid-cols-3'>
                  {[
                    ['Account Status', profile.account_status || 'N/A'],
                    ['User ID', profile.user_id || 'N/A'],
                    ['Email', profile.email || 'N/A'],
                    ['Phone', profile.phone || 'N/A'],
                    ['Country', profile.country_code || profile.location || 'N/A'],
                    ['Email Verified', profile.is_email_verified ? 'Yes' : 'No'],
                    ['Phone Verified', profile.is_phone_verified ? 'Yes' : 'No'],
                    ['Age Verified', profile.is_age_verified ? 'Yes' : 'No'],
                    ['Followers', formatNumber(profile.followers_count)],
                    ['Following', formatNumber(profile.following_count)],
                    ['Total Likes', formatNumber(profile.total_likes)],
                    ['Joined', formatDate(profile.created_at)],
                  ].map(([label, val]) => (
                    <DetailRow key={label} label={label} value={val} />
                  ))}
                </div>
              </SectionShell>
            )}
          </div>
        </div>

        <div className='mt-6 grid gap-6 lg:grid-cols-2'>
          <PayoutMethodsPanel methods={payoutMethods} />
          <PolicyViolationsPanel violations={policyViolations} />
        </div>

        {(notableTokenTransactions.length > 0 || notableCoinTransactions.length > 0) && (
          <div className='mt-6 grid gap-6'>
            <TransactionTable title='Notable Token Transactions' transactions={notableTokenTransactions} unitLabel='token' />
            <TransactionTable title='Notable Coin Transactions' transactions={notableCoinTransactions} unitLabel='coin' />
          </div>
        )}

        <div className='mt-6 grid gap-6'>
          <TransactionTable title='Token Transaction History' transactions={tokenTransactions} unitLabel='token' />
          <TransactionTable title='Coin Transaction History' transactions={coinTransactions} unitLabel='coin' />
        </div>

        {/* Withdrawal History */}
        <div className='mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
          <div className='flex items-center justify-between border-b border-gray-200 px-5 py-4'>
            <div>
              <h2 className='text-base font-semibold text-gray-900'>Withdrawal History</h2>
              <p className='text-sm text-gray-500'>All cashout requests made by this user.</p>
            </div>
            <button
              onClick={loadWithdrawals}
              className='rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50'
            >
              Refresh
            </button>
          </div>

          <div className='grid gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 sm:grid-cols-2 lg:grid-cols-6'>
            <BalanceMetric label='Lifetime Requests' value={lifetimeWithdrawalSummary.total_requests} />
            <BalanceMetric label='Lifetime Tokens' value={lifetimeWithdrawalSummary.total_tokens} />
            <BalanceMetric label='Processing' value={lifetimeWithdrawalSummary.processing_count} />
            <BalanceMetric label='Completed' value={lifetimeWithdrawalSummary.completed_count} />
            <BalanceMetric label='Failed / Cancelled' value={lifetimeWithdrawalSummary.failed_count + lifetimeWithdrawalSummary.cancelled_count} />
            <div className='rounded-lg border border-gray-200 bg-white px-3 py-3'>
              <p className='text-xs font-medium text-gray-500'>Lifetime Net USD</p>
              <p className='mt-1 text-lg font-bold text-gray-800'>{formatCurrency(lifetimeWithdrawalSummary.total_net_usd)}</p>
            </div>
          </div>

          <div className='grid gap-2 border-b border-gray-100 bg-white px-5 py-4 text-sm sm:grid-cols-2 lg:grid-cols-4'>
            <DetailRow label='Completed Tokens' value={formatNumber(lifetimeWithdrawalSummary.completed_tokens)} />
            <DetailRow label='Processing Tokens' value={formatNumber(lifetimeWithdrawalSummary.processing_tokens)} />
            <DetailRow label='First Cashout' value={formatDate(lifetimeWithdrawalSummary.first_created_at)} />
            <DetailRow label='Last Cashout' value={formatDate(lifetimeWithdrawalSummary.last_created_at)} />
          </div>

          {wdLoading ? (
            <div className='flex items-center justify-center py-10'>
              <div className='h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
            </div>
          ) : withdrawals.length === 0 ? (
            <div className='px-5 py-8 text-center text-sm text-gray-400'>No withdrawal requests found for this user.</div>
          ) : (
            <>
              <div ref={withdrawalTableRef} className='max-h-[520px] overflow-y-auto'>
                <table className='w-full table-fixed divide-y divide-gray-200'>
                  <thead className='sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_rgba(229,231,235,1)]'>
                    <tr>
                      <th className='w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reference</th>
                      <th className='w-[11%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Method</th>
                      <th className='w-[10%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Tokens</th>
                      <th className='w-[8%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Gross</th>
                      <th className='w-[8%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Fees</th>
                      <th className='w-[9%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Net USD</th>
                      <th className='w-[10%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                      <th className='w-[16%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Created</th>
                      <th className='w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 bg-white'>
                    {withdrawals.map((wd) => {
                      const isProcessing = wd.status === 'processing';
                      const isCompleteLoading = wdActionLoading === `${wd.request_id}:complete`;
                      const isFailLoading = wdActionLoading === `${wd.request_id}:fail`;
                      const isExpanded = failingRequestId === wd.request_id;
                      const totalFees = (wd.platform_fee_usd + wd.payout_fee_usd).toFixed(2);
                      const showPreview = !isProcessing && (wd.status === 'failed' || wd.status === 'cancelled');
                      return (
                        <React.Fragment key={wd.request_id}>
                          <tr className='transition-colors hover:bg-gray-50/50'>
                            <td className='break-all px-3 py-3.5 font-mono text-xs text-gray-700'>{wd.reference_id}</td>
                            <td className='px-3 py-3.5 text-sm'>
                              <p className='text-gray-700 capitalize'>{wd.method_type}</p>
                              {wd.method_label && <p className='text-xs text-gray-400'>{wd.method_label}</p>}
                            </td>
                            <td className='px-3 py-3.5 text-sm text-gray-700'>{wd.tokens.toLocaleString()}</td>
                            <td className='px-3 py-3.5 text-sm text-gray-700'>${wd.gross_usd.toFixed(2)}</td>
                            <td className='px-3 py-3.5 text-sm text-gray-500'>
                              <span title={`Platform: $${wd.platform_fee_usd.toFixed(2)} + Payout: $${wd.payout_fee_usd.toFixed(2)}`}>
                                -${totalFees}
                              </span>
                            </td>
                            <td className='px-3 py-3.5 text-sm font-semibold text-gray-900'>${wd.net_usd.toFixed(2)}</td>
                            <td className='px-3 py-3.5 text-sm'>
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getWdStatusBadgeClass(wd.status)}`}>
                                {wd.status}
                              </span>
                            </td>
                            <td className='px-3 py-3.5 text-sm text-gray-500'>{formatDate(wd.created_at)}</td>
                            <td className='px-3 py-3.5 text-sm'>
                              {isProcessing ? (
                                <div className='flex flex-wrap items-center gap-2'>
                                  <button
                                    onClick={() => handleApproveWithdrawal(wd.request_id)}
                                    disabled={Boolean(wdActionLoading) || isExpanded}
                                    className='rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isCompleteLoading ? 'Marking…' : 'Complete'}
                                  </button>
                                  <button
                                    onClick={() => { setFailingRequestId(isExpanded ? null : wd.request_id); setFailReason(''); }}
                                    disabled={Boolean(wdActionLoading)}
                                    className='rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isFailLoading ? 'Failing…' : 'Fail'}
                                  </button>
                                </div>
                              ) : (
                                <span className='text-xs font-medium text-gray-400'>—</span>
                              )}
                            </td>
                          </tr>

                          {/* Fail reason picker — shown while marking as failed */}
                          {isExpanded && (
                            <tr className='bg-rose-50'>
                              <td colSpan={9} className='px-5 py-4'>
                                <div className='flex items-center gap-3'>
                                  <select
                                    value={failReason}
                                    onChange={(e) => setFailReason(e.target.value)}
                                    className='flex-1 rounded-lg border border-rose-300 px-3 py-2 text-sm text-gray-700 focus:border-rose-500 focus:outline-none'
                                  >
                                    <option value=''>— Select failure reason —</option>
                                    {WITHDRAWAL_FAIL_REASONS.map((r) => (
                                      <option key={r} value={r}>{r}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleFailWithdrawal(wd.request_id)}
                                    disabled={Boolean(wdActionLoading)}
                                    className='rounded-md bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isFailLoading ? 'Submitting…' : 'Confirm Fail'}
                                  </button>
                                  <button
                                    onClick={() => { setFailingRequestId(null); setFailReason(''); }}
                                    className='rounded-md border border-gray-300 px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50'
                                  >
                                    Cancel
                                  </button>
                                </div>
                                {failReason && (
                                  <div className='mt-4'>
                                    <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>User will see →</p>
                                    <WithdrawalStatusMessage
                                      status='failed'
                                      failureReason={failReason}
                                      grossUsd={wd.gross_usd}
                                      referenceId={wd.reference_id}
                                      createdAt={wd.created_at}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}

                          {/* User-facing message preview for already-failed/cancelled rows */}
                          {showPreview && (
                            <tr>
                              <td colSpan={9} className='px-5 pb-4 pt-0'>
                                <WithdrawalStatusMessage
                                  status={wd.status}
                                  failureReason={wd.failure_reason}
                                  processedAt={wd.processed_at}
                                  grossUsd={wd.gross_usd}
                                  referenceId={wd.reference_id}
                                  createdAt={wd.created_at}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <TableScrollFooter
                label={`${withdrawals.length} withdrawal rows in this table`}
                onPrevious={() => scrollTableRows(withdrawalTableRef, -1)}
                onNext={() => scrollTableRows(withdrawalTableRef, 1)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashoutApplicationDetailPage;
